// ── Zone Activity page data ───────────────────────────────────────────────
// Zones, devices, drivers and groups, loaded once per session and cached.
// Zone rings are prepared (bbox + Float64Array coordinates) and their card
// plates precomputed here so neither the analysis nor the live refresh ever
// touches raw geometry again.

import { callGeotabAPI } from '../api/geotab-client';
import { buildGroupHierarchy } from '../api/group-hierarchy';
import type { GroupHierarchy, GroupNode } from '../api/group-hierarchy';
import type { Device, Group, Rule, RuleConditionNode, User, Zone, ZoneType } from '../api/types';
import { ensureSessionTimeZone } from '../utils/timezone';
import { buildPlate } from './zone-activity-plate';
import { buildZoneGrid } from './zone-activity-geometry';
import type { ZoneGrid } from './zone-activity-geometry';
import type { PreparedZone } from './zone-activity-types';

const ZONE_LIMIT      = 10000;
const DEVICE_LIMIT    = 5000;
const USER_LIMIT      = 5000;
const GROUP_LIMIT     = 1000;
const ZONE_TYPE_LIMIT = 1000;
const RULE_LIMIT      = 10000;

/**
 * Every distinct zone id referenced anywhere in a rule's condition tree
 * (a `Get Rule` returns the full nested tree — no search flag needed, same
 * as Trip.stopPoint and Zone.points).
 */
function zoneIdsInCondition(node: RuleConditionNode | undefined, out: Set<string>): void {
  if (!node) return;
  if (node.zone?.id) out.add(node.zone.id);
  (node.children ?? []).forEach(c => zoneIdsInCondition(c, out));
}

/**
 * Maps a zone id to the ZoneStop rule(s) whose condition targets it.
 * ONLY baseType 'ZoneStop' rules qualify — they are the rules Geotab's own
 * Zone Visit reports read, and their events mean exactly "vehicle stopped in
 * this zone". A custom rule that happens to reference one zone could mean
 * anything (speeding inside it, idling inside it, after-hours presence…),
 * so counting its events as visits would silently corrupt a "verified"
 * number; such zones stay on the estimate path instead. A rule referencing
 * zero or multiple distinct zones is skipped for the same reason —
 * ExceptionEvent carries no zone field, so its events can't be attributed.
 */
function buildZoneRuleMap(rules: Rule[]): Map<string, string[]> {
  const now = new Date();
  const map = new Map<string, string[]>();
  const seen = new Set<string>();
  for (const r of rules) {
    if (!r?.id || seen.has(r.id)) continue;
    seen.add(r.id);
    if (r.baseType !== 'ZoneStop') continue;
    if (r.activeTo && new Date(r.activeTo) < now) continue;
    const zoneIds = new Set<string>();
    zoneIdsInCondition(r.condition, zoneIds);
    if (zoneIds.size !== 1) continue;
    const zoneId = [...zoneIds][0];
    const list = map.get(zoneId);
    if (list) list.push(r.id);
    else map.set(zoneId, [r.id]);
  }
  return map;
}

/**
 * ZoneStop rules — THE rules Geotab's own Zone Visit reports are built on —
 * are a separate rule category (ZoneStopExceptionRules) that a plain
 * `Get Rule` does NOT return; they must be requested explicitly by baseType.
 * This was found the hard way: a database whose "Advanced Zone Visit
 * Details" export named a rule per zone still produced an empty rule list
 * from the bare call, silently downgrading every zone to "estimated".
 * Geotab auto-creates one such rule per zone whose MustIdentifyStops flag is
 * on. A failure here degrades to the estimate path rather than failing the
 * page; buildZoneRuleMap re-checks baseType in case a server ignores the
 * search filter and returns everything.
 */
async function fetchZoneStopRules(): Promise<Rule[]> {
  return callGeotabAPI<Rule[]>('Get', {
    typeName: 'Rule',
    search: { baseType: 'ZoneStop' },
    resultsLimit: RULE_LIMIT,
  }).catch((e): Rule[] => { console.warn('Zone Activity: ZoneStop rule fetch failed', e); return []; });
}

export interface ZoneDevice {
  id: string;
  name: string;
  groupIds: string[];
}

/**
 * Type-filter option values are tagged rather than raw names: '' means all,
 * 'u:' means "no type assigned", and a named type is 't:<name>'. Zone type
 * names are user-entered, so an untagged sentinel could collide with a real
 * one — tagging makes that structurally impossible.
 */
export const ZONE_TYPE_FILTER_UNTYPED = 'u:';
export const zoneTypeFilterValue = (name: string): string => `t:${name}`;

/** One option in the zone-type filter: every type in the database, used or not. */
export interface ZoneTypeOption {
  name: string;
  /** Zones carrying this type. 0 is legitimate — a defined-but-unused type. */
  count: number;
  isBuiltIn: boolean;
}

/**
 * Geotab's shipped zone types come back from `Get ZoneType` with `isBuiltIn`
 * set, but their `name` is a localisation key resolved by the MyGeotab UI
 * rather than a plain string — so reading `name` alone can yield blank, and
 * falling back to the raw id puts "ZoneTypeCustomerId" in a user-facing
 * dropdown. These are the three types every database ships with — a database
 * can have MORE built-in types than this (e.g. "In-house Service Center",
 * "Vendor Service Center" — MyGeotab > Zone Types), and this three-entry list
 * was silently downgrading every one of those to a raw id in the filter.
 * Anything not listed here now falls through to prettifyZoneTypeId() below
 * instead of showing an internal id.
 */
const BUILT_IN_ZONE_TYPE_NAMES: Record<string, string> = {
  ZoneTypeCustomerId: 'Customer',
  ZoneTypeHomeId:     'Home',
  ZoneTypeOfficeId:   'Office',
};

/** "ZoneTypeVendorServiceCenterId" -> "Vendor Service Center". An id that doesn't match
 *  Geotab's `ZoneType<Name>Id` pattern (a custom type's id never does) is returned as-is —
 *  callers only reach this once `t.name` has already been checked and found unusable. */
function prettifyZoneTypeId(id: string): string {
  const m = /^ZoneType(.+)Id$/.exec(id);
  const core = m ? m[1] : id;
  const spaced = core
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')    // camel/Pascal word boundary: "Vendor|Service"
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')  // acronym then a word: "GPS|Tracker"
    .trim();
  return spaced || id;
}

/** Matches a Geotab localisation-key string, e.g. "ZoneTypeInHouseServiceCenterId" — the same
 *  shape as a built-in ZoneType's `.id`. When `.id` is missing, `.name` carries this key
 *  instead of a translated string, so a bare truthiness check on `.name` isn't enough to tell
 *  "a real name" apart from "the untranslated key" — this is that check. */
const ZONE_TYPE_KEY_PATTERN = /^ZoneType.+Id$/;

/** Display name for a zone type: its own name if Geotab returned a real one, else a known
 *  built-in label, else its id (or key-shaped name) prettified into words — never a raw
 *  internal id or an untranslated localisation key. */
function zoneTypeName(t: ZoneType): string {
  const own = (t.name || '').trim();
  const byKey = BUILT_IN_ZONE_TYPE_NAMES[own] ?? BUILT_IN_ZONE_TYPE_NAMES[t.id];
  if (byKey) return byKey;
  if (own && !ZONE_TYPE_KEY_PATTERN.test(own)) return own;
  return prettifyZoneTypeId(own || t.id);
}

export interface ZoneActivityPageData {
  zones: PreparedZone[];
  grid: ZoneGrid;
  devices: ZoneDevice[];
  deviceNames: Map<string, string>;
  /**
   * id → display name for EVERY active user, drivers and non-drivers alike.
   * A zone visit is attributed to whoever was assigned to the vehicle, which
   * is not always a flagged driver, so narrowing this would make those visits
   * render as raw user ids. Use `drivers` for anything user-facing that must
   * offer a choice of driver.
   */
  userMap: Map<string, string>;
  /** Users flagged isDriver, for the "By Driver" picker. Sorted by display name. */
  drivers: Array<{ id: string; name: string }>;
  groupMap: Map<string, string>;
  groups: GroupNode[];
  groupChildren: Map<string, string[]>;
  /** Every zone type defined in the database, with how many zones carry it. Drives the type filter. */
  zoneTypes: ZoneTypeOption[];
  /**
   * Zones with no zone type at all. Tracked separately because they belong to
   * no type bucket, so without it the filter's counts silently fail to
   * reconcile against the total zone count.
   */
  untypedZoneCount: number;
  /** The Zone fetch came back at the cap — some zones are missing. */
  zonesTruncated: boolean;
  /**
   * The Device fetch came back at the cap. This list is the authority on which
   * devices are real/active (the live layer drops anything absent from it), so
   * a truncated fetch means active vehicles go missing from the page entirely
   * rather than merely being unnamed — never let that pass silently.
   */
  devicesTruncated: boolean;
  /** Zones dropped because their ring was unusable (fewer than 3 points). */
  skippedZoneCount: number;
  /** Zones with at least one matched Rule — their visits will be exact, not estimated. */
  ruleBackedZoneCount: number;
}

let _cached: ZoneActivityPageData | null = null;

export function resetZoneActivityData(): void {
  _cached = null;
}

/**
 * Turn a raw Geotab zone into a PreparedZone, or null if its ring is unusable.
 * Geotab may or may not repeat the first point to close the ring; the closing
 * edge is implicit everywhere downstream, so a repeated tail point is dropped.
 *
 * `zoneTypeMap` resolves each zoneType reference to its display name. `Zone`'s
 * nested `zoneTypes` entries aren't reliably self-describing — some come back
 * as a bare `{id}` with no inline `name` — so a type with no resolvable name
 * previously vanished silently (`.filter(Boolean)` on an empty string), which
 * both blanked the card's type chip and meant that type could never appear in
 * the filter dropdown. Falling back to the id keeps every type visible even
 * in the worst case, rather than dropping it.
 */
function prepareZone(z: Zone, zoneTypeMap: Map<string, string>, zoneRuleMap: Map<string, string[]>): PreparedZone | null {
  const pts = z.points;
  if (!pts || pts.length < 3) return null;

  let n = pts.length;
  const first = pts[0];
  const last  = pts[n - 1];
  if (n > 3 && first.x === last.x && first.y === last.y) n -= 1;
  if (n < 3) return null;

  const lat = new Float64Array(n);
  const lng = new Float64Array(n);
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;

  for (let i = 0; i < n; i++) {
    const p = pts[i];
    if (typeof p?.x !== 'number' || typeof p?.y !== 'number') return null;
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
    lng[i] = p.x;
    lat[i] = p.y;
    if (p.y < minLat) minLat = p.y;
    if (p.y > maxLat) maxLat = p.y;
    if (p.x < minLng) minLng = p.x;
    if (p.x > maxLng) maxLng = p.x;
  }

  return {
    id: z.id,
    name: (z.name || '').trim() || z.id,
    typeNames: (z.zoneTypes ?? [])
      .map(raw => {
        // Same mixed shape as the ZoneType catalog fetch above (verified live): a zone's own
        // reference to a custom type is an object ({id, name}), but its reference to a
        // built-in type is a bare string (the type's own id, e.g. "ZoneTypeVendorServiceCenterId")
        // -- not an object at all. Reading .id/.name off a string silently produced undefined,
        // which got filtered out below, so any zone whose ONLY type was a built-in one lost its
        // type entirely and miscounted as "No type assigned" even though Geotab correctly shows
        // it tagged. zoneTypeMap is keyed by this same string for built-ins (see above), so
        // normalising here the same way resolves it to the real display name.
        const t: { id?: string; name?: string } =
          typeof raw === 'string' ? { id: raw } : ((raw as { id?: string; name?: string } | undefined) ?? {});
        return (t.id && zoneTypeMap.get(t.id)) || (t.name || '').trim() || t.id;
      })
      .filter((name): name is string => !!name),
    comment: (z.comment || '').trim(),
    lat, lng, minLat, maxLat, minLng, maxLng,
    plate: buildPlate(lat, lng),
    ruleIds: zoneRuleMap.get(z.id) ?? [],
  };
}

export async function loadZoneActivityPageData(
  forceRefresh = false,
): Promise<ZoneActivityPageData> {
  if (_cached && !forceRefresh) return _cached;

  // Resolved before anything renders so every timestamp on this page is on
  // the user's MyGeotab profile clock, not the browser's.
  await ensureSessionTimeZone();

  const [rawZones, rawDevices, rawUsers, rawGroups, rawZoneTypes, rawRules] = await Promise.all([
    callGeotabAPI<Zone[]>    ('Get', { typeName: 'Zone',     resultsLimit: ZONE_LIMIT      }),
    callGeotabAPI<Device[]>  ('Get', { typeName: 'Device',   resultsLimit: DEVICE_LIMIT     }),
    callGeotabAPI<User[]>    ('Get', { typeName: 'User',     resultsLimit: USER_LIMIT       }),
    callGeotabAPI<Group[]>   ('Get', { typeName: 'Group',    resultsLimit: GROUP_LIMIT      }),
    callGeotabAPI<ZoneType[]>('Get', { typeName: 'ZoneType', resultsLimit: ZONE_TYPE_LIMIT  }),
    fetchZoneStopRules(),
  ]);

  const now = new Date();
  const zoneRuleMap = buildZoneRuleMap(rawRules ?? []);

  // The authoritative id → name map for zone types, and the master list for
  // the filter dropdown — every type defined in the database, built-in ones
  // included, not just the ones assigned to a currently-loaded zone.
  const zoneTypeMap = new Map<string, string>();
  const builtInByName = new Map<string, boolean>();
  (rawZoneTypes ?? []).forEach(raw => {
    // `Get ZoneType` mixes two shapes in the same array — confirmed with a live console dump,
    // not assumed: a custom type is a real object ({id, name, isBuiltIn, comment, fillColor}),
    // but a built-in type is a bare STRING — its own id/localisation-key, e.g.
    // "ZoneTypeCustomerId" or "ZoneTypeVendorServiceCenterId" — not an object at all. Treating
    // every entry as an object (the `ZoneType` interface's assumption, and the Geotab norm for
    // every other entity) meant every built-in type read as {id: undefined, name: undefined,
    // isBuiltIn: undefined} and was silently dropped — not mislabeled, entirely absent.
    const t: { id?: string; name?: string; isBuiltIn?: boolean } =
      typeof raw === 'string' ? { id: raw, isBuiltIn: true } : ((raw as ZoneType | undefined) ?? {});
    const key = t.id || (t.name || '').trim();
    if (!key) return;
    const name = zoneTypeName(t as ZoneType);
    zoneTypeMap.set(key, name);
    // A name collision between a built-in and a custom type resolves to
    // "built-in" only if nothing custom claims it, so the badge can't lie.
    builtInByName.set(name, (builtInByName.get(name) ?? true) && t.isBuiltIn === true);
  });

  // Zones: keep only active ones with a usable ring.
  const zones: PreparedZone[] = [];
  let skippedZoneCount = 0;
  for (const z of rawZones ?? []) {
    if (!z?.id) continue;
    if (z.activeTo && new Date(z.activeTo) < now) continue;
    const prepared = prepareZone(z, zoneTypeMap, zoneRuleMap);
    if (prepared) zones.push(prepared);
    else skippedZoneCount++;
  }
  zones.sort((a, b) => a.name.localeCompare(b.name));

  // How many zones carry each type, so the filter can show its yield up front.
  const typeCounts = new Map<string, number>();
  zones.forEach(z => z.typeNames.forEach(t => typeCounts.set(t, (typeCounts.get(t) ?? 0) + 1)));

  // Union of the catalog and what zones actually reference: a defined type
  // with no zones stays listed (0), and a type referenced by a zone but absent
  // from the catalog (shouldn't happen) must never become unfilterable.
  const zoneTypes: ZoneTypeOption[] = [...new Set([...builtInByName.keys(), ...typeCounts.keys()])]
    .map(name => ({
      name,
      count: typeCounts.get(name) ?? 0,
      isBuiltIn: builtInByName.get(name) ?? false,
    }))
    // Types actually in use first — the filter's productive options lead.
    .sort((a, b) => (b.count > 0 ? 1 : 0) - (a.count > 0 ? 1 : 0) || a.name.localeCompare(b.name));

  // Devices: active, telematics-equipped assets only (house filter).
  const devices: ZoneDevice[] = (rawDevices ?? [])
    .filter(d => d?.id && d.name && d.serialNumber && (!d.activeTo || new Date(d.activeTo) > now))
    .map(d => ({
      id: d.id,
      name: d.name,
      groupIds: (d.groups ?? []).map(g => g.id).filter(Boolean),
    }));
  const deviceNames = new Map(devices.map(d => [d.id, d.name]));

  // Display names for EVERY active user — see the userMap doc comment: a visit
  // can be attributed to a non-driver, and that name still has to resolve.
  const userMap = new Map<string, string>();
  const drivers: Array<{ id: string; name: string }> = [];
  (rawUsers ?? []).forEach(u => {
    if (!u?.id || u.id === 'UnknownDriverId') return;
    if (u.activeTo && new Date(u.activeTo) < now) return;
    const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    const name = full || u.name || u.id;
    userMap.set(u.id, name);
    // The "By Driver" picker offers only actual drivers — a list of every
    // office login is unusable for choosing whose route to analyse.
    if (u.isDriver === true) drivers.push({ id: u.id, name });
  });
  drivers.sort((a, b) => a.name.localeCompare(b.name));

  const hierarchy: GroupHierarchy = buildGroupHierarchy(rawGroups ?? []);

  _cached = {
    zones,
    grid: buildZoneGrid(zones),
    devices,
    deviceNames,
    userMap,
    drivers,
    groupMap: hierarchy.groupMap,
    groups: hierarchy.groups,
    groupChildren: hierarchy.groupChildren,
    zoneTypes,
    untypedZoneCount: zones.reduce((n, z) => n + (z.typeNames.length ? 0 : 1), 0),
    zonesTruncated: (rawZones?.length ?? 0) >= ZONE_LIMIT,
    devicesTruncated: (rawDevices?.length ?? 0) >= DEVICE_LIMIT,
    skippedZoneCount,
    ruleBackedZoneCount: zones.reduce((n, z) => n + (z.ruleIds.length > 0 ? 1 : 0), 0),
  };
  return _cached;
}
